import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { SearchInput } from '@ds';
import { AdminPageHeader, ConfirmationModal, DataTable } from '@ui';
import { toast } from 'react-toastify';
import { AuthContext } from '@lp/core/auth';
import {
  EMPLOYEES_LIST_FILTERS_BASE,
  getEmployeesTableColumns,
  getRenderEmployeesTableCell,
} from '@lp/features/admin/tables/EmployeesListTable';
import { EmployeesFilters } from '@lp/features/admin/tables/EmployeesListTable/components/EmployeeFilters';
import { EmployeeComposed, EmployeeType } from '@/api/apis/resources';
import {
  useActivateEmployeeMutation,
  useDeactivateEmployeeMutation,
  useActivateCSMutation,
  useActivateCLMutation,
  useDeactivateCSMutation,
  useDeactivateCLMutation,
  useEmployeesListQuery,
} from '@/queries/employee';
import { useFilterQueryWithParams } from '@/hooks/useFilterQueryWithParams';
import { retrieveUnifiedError } from '@/api/utils';
import { AdminPermissions } from '@/permissions';

type EmployeesListProps = {
  type: EmployeeType;
};

export const EmployeesList = ({ type }: EmployeesListProps) => {
  const [employeeToChange, setEmployeeToChange] = useState<EmployeeComposed | undefined>();
  const [confirmationModalTitle, setConfirmationModalTitle] = useState<string>('');
  const [confirmationModalDesc, setConfirmationModalDesc] = useState<string>('');
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false);
  const { mutateAsync: activateEmployeeAsync } = useActivateEmployeeMutation();
  const { mutateAsync: activateCSAsync } = useActivateCSMutation();
  const { mutateAsync: activateCLAsync } = useActivateCLMutation();
  const { mutateAsync: deactivateEmployeeAsync } = useDeactivateEmployeeMutation();
  const { mutateAsync: deactivateCSAsync } = useDeactivateCSMutation();
  const { mutateAsync: deactivateCLAsync } = useDeactivateCLMutation();
  const isContractorList = type === EmployeeType.contractor;
  const { checkPermission } = useContext(AuthContext);

  const hasEmployeeEditPermission = checkPermission(AdminPermissions.TmdxUsersUpdate);
  const hasCSEditPermission = checkPermission(AdminPermissions.TmdxUsersCSUpdate);
  const hasCLEditPermission = checkPermission(AdminPermissions.TmdxUsersCLUpdate);

  const employeeMutations = useMemo(() => {
    if (hasCSEditPermission) {
      return {
        activate: activateCSAsync,
        deactivate: deactivateCSAsync,
      };
    }

    if (hasCLEditPermission) {
      return {
        activate: activateCLAsync,
        deactivate: deactivateCLAsync,
      };
    }

    return {
      activate: activateEmployeeAsync,
      deactivate: deactivateEmployeeAsync,
    };
  }, [
    activateCLAsync,
    activateCSAsync,
    activateEmployeeAsync,
    deactivateCLAsync,
    deactivateCSAsync,
    deactivateEmployeeAsync,
    hasCLEditPermission,
    hasCSEditPermission,
  ]);

  const {
    data,
    isLoading,
    isFetching,
    filters,
    hasFilters,
    updateFilters,
    clearFilters,
    page,
    itemsPerPage,
    searchText,
    sortBy,
    setPage,
    setItemsPerPage,
    setSearchText,
    setSortBy,
  } = useFilterQueryWithParams({
    filterList: { ...EMPLOYEES_LIST_FILTERS_BASE, isContractor: isContractorList },
    query: useEmployeesListQuery,
  });

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const onIsActiveSwitchChanged = useCallback((employee: EmployeeComposed) => {
    setConfirmationModalTitle(
      `${employee.isActive ? 'Deactivate' : 'Activate'} TMDX ${employee.isContractor ? 'Contractor' : 'Employee'}?`,
    );
    setConfirmationModalDesc(
      `Are you sure you want to ${employee.isActive ? 'deactivate' : 'activate'} the TMDX ${employee.isContractor ? 'Contractor' : 'Employee'}?`,
    );
    setEmployeeToChange(employee);
  }, []);

  useEffect(() => {
    if (employeeToChange) {
      setConfirmationModalOpen(true);
    }
  }, [employeeToChange]);

  const onChangeActive = async () => {
    if (!employeeToChange) {
      toast.error(`No ${isContractorList ? 'contractor' : 'employee'} selected`);
      return;
    }

    try {
      if (employeeToChange.isActive) {
        await employeeMutations.deactivate(employeeToChange.id);
      } else {
        await employeeMutations.activate(employeeToChange.id);
      }
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  const onConfirmationModalClose = useCallback(() => {
    setEmployeeToChange(undefined);
    setConfirmationModalOpen(false);
  }, []);

  const TABLE_COLUMNS = useMemo(() => getEmployeesTableColumns(), []);
  const pageTitle = isContractorList ? 'Contractors' : 'Employees';

  const renderTableCell = useMemo(
    () =>
      getRenderEmployeesTableCell({
        type,
        hasEmployeeEditPermission,
        hasCSEditPermission,
        hasCLEditPermission,
        onIsActiveChanged: onIsActiveSwitchChanged,
      }),
    [type, onIsActiveSwitchChanged, hasEmployeeEditPermission, hasCSEditPermission, hasCLEditPermission],
  );

  return (
    <div className="flex flex-col place-items-stretch gap-9">
      <AdminPageHeader title={pageTitle}>
        <div className="max-w-[480px] grow">
          <SearchInput placeholder="Search" value={searchText} onChange={onSearchChange} inputVariant="secondary" />
        </div>
      </AdminPageHeader>
      <div className="flex flex-col gap-4">
        <EmployeesFilters
          filters={filters}
          hasFilters={hasFilters}
          updateFilters={updateFilters}
          clearFilters={clearFilters}
        />
        <DataTable
          label={pageTitle}
          columns={TABLE_COLUMNS}
          renderCell={renderTableCell}
          data={data?.items || []}
          isLoading={isLoading}
          isFetching={isFetching}
          totalItems={data?.totalItems || 0}
          totalPages={data?.totalPages || 1}
          page={page}
          itemsPerPage={itemsPerPage}
          sortBy={sortBy}
          onPageChange={setPage}
          onItemsPerPageChange={setItemsPerPage}
          onSortByChange={setSortBy}
        />
      </div>
      <ConfirmationModal
        title={confirmationModalTitle}
        description={confirmationModalDesc}
        isOpen={confirmationModalOpen}
        onChangeActive={onChangeActive}
        onClose={onConfirmationModalClose}
      />
    </div>
  );
};
